import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { fmtEUR, fmtDate } from "../../lib/format";
import { Card } from "../ui/card";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { ArrowRight, Download, Eye, GitBranch, Loader2 } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { toast } from "sonner";

const Riga = ({ l, v, cls }) => <div className="flex justify-between gap-2 text-xs"><span className="text-muted-foreground">{l}</span><span className={`font-medium text-right ${cls || ""}`}>{v}</span></div>;

function Passo({ r }) {
  const chiuso = r.concluso || r.stato === "concluso";
  return (
    <div className={`rounded-lg border p-3 min-w-[230px] flex-1 space-y-1.5 ${r.ultimo && !chiuso ? "border-primary bg-primary/5" : "bg-card"}`} data-testid="catena-passo">
      <div className="flex items-center justify-between gap-2"><div className="text-[10px] uppercase tracking-wide text-muted-foreground">Passo {r.n}</div><Badge variant={chiuso ? "outline" : "default"} className="capitalize text-[10px]">{r.ultimo && !chiuso ? "in corso" : r.stato}</Badge></div>
      <div className="font-semibold text-sm leading-tight">{r.operazione}</div>
      <div className="text-[11px] text-muted-foreground">{r.codice} · {fmtDate(r.sottoscrizione)}{r.chiusura ? ` → ${fmtDate(r.chiusura)}` : ""}</div>
      <Riga l="Apporto" v={fmtEUR(r.apporto)} />
      {r.di_cui_utili_reinvestiti > 0 && <Riga l="di cui utili reinvestiti" v={fmtEUR(r.di_cui_utili_reinvestiti)} cls="text-sky-800" />}
      <Riga l={`Quota utili lorda${r.utili_stimati ? " (prevista)" : ""}`} v={fmtEUR(r.utili_lordi)} cls="text-emerald-700" />
      <Riga l="Ritenuta riportata in ingresso" v={fmtEUR(r.ritenuta_riportata_in_ingresso)} cls="text-amber-700" />
      {(r.pagato_capitale > 0 || r.pagato_utili > 0) && <Riga l="Pagato netto a te" v={fmtEUR(r.pagato_capitale + r.pagato_utili - r.ritenuta_pagata)} cls="text-primary" />}
      {r.storico && <div className="text-[10px] text-muted-foreground italic">collegamento storico</div>}
    </div>
  );
}

function Catena({ id }) {
  const [d, setD] = useState(null);
  const [pdf, setPdf] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get(`/investments/${id}/catena`).then((r) => setD(r.data)).catch(() => {}); }, [id]);
  const apri = async (download) => {
    setBusy(true);
    try {
      const r = await api.get(`/investments/${id}/rendiconto-reinvestimento`, { responseType: "blob" });
      const u = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      if (download) { const a = document.createElement("a"); a.href = u; a.download = `rendiconto_${d?.ultimo?.codice || id}.pdf`; a.click(); } else setPdf(u);
    } catch { toast.error("Rendiconto non disponibile"); } finally { setBusy(false); }
  };
  if (!d) return null;
  const grafico = d.righe.map((r) => ({ nome: `${r.n}. ${r.operazione || r.codice}`, Apporto: r.apporto, "Utili lordi": r.utili_lordi, "Ritenuta riportata": r.ritenuta_riportata_in_ingresso }));
  const u = d.ultimo;
  return (
    <Card className="p-4 space-y-4" data-testid="portal-catena">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="font-semibold flex items-center gap-2"><GitBranch className="w-4 h-4 text-primary" /> La tua catena di reinvestimenti · {d.righe.length} {d.righe.length === 1 ? "passaggio" : "passaggi"}</div>
          <div className="text-xs text-muted-foreground mt-1">Ad ogni chiusura capitale e quota utili lorda sono passati nell'operazione successiva senza bonifico. La ritenuta del 26% sugli utili reinvestiti viene applicata solo al primo pagamento effettivo.</div>
        </div>
        <div className="flex gap-2">
          <Button size="sm" onClick={() => apri(false)} disabled={busy} data-testid="catena-vedi-rendiconto">{busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Eye className="w-4 h-4 mr-1" />}Vedi rendiconto</Button>
          <Button size="sm" variant="outline" onClick={() => apri(true)} disabled={busy} data-testid="catena-scarica-rendiconto"><Download className="w-4 h-4 mr-1" />PDF</Button>
        </div>
      </div>
      <div className="flex items-stretch gap-2 overflow-x-auto pb-1" data-testid="catena-passi">
        {d.righe.map((r, i) => (<React.Fragment key={r.id}>
          <Passo r={r} />
          {i < d.righe.length - 1 && <div className="flex flex-col items-center justify-center text-[10px] text-sky-800 shrink-0 w-24 text-center">
            <ArrowRight className="w-5 h-5" />
            <div>{fmtEUR((r.reinvestito_capitale || d.righe[i + 1].apporto - (d.righe[i + 1].di_cui_utili_reinvestiti || 0)) + (r.reinvestito_utili || d.righe[i + 1].di_cui_utili_reinvestiti || 0))}</div>
            <div className="text-muted-foreground">reinvestiti</div>
          </div>}
        </React.Fragment>))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 h-56" data-testid="catena-grafico">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={grafico} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
              <XAxis dataKey="nome" tick={{ fontSize: 10 }} interval={0} /><YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `${Math.round(v / 1000)}k`} width={38} />
              <Tooltip formatter={(v) => fmtEUR(v)} /><Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="Apporto" fill="#1f2a44" radius={[3, 3, 0, 0]} /><Bar dataKey="Utili lordi" fill="#059669" radius={[3, 3, 0, 0]} /><Bar dataKey="Ritenuta riportata" fill="#d97706" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="rounded-lg border bg-muted/30 p-3 space-y-2" data-testid="catena-posizione">
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Posizione attuale</div>
          <div className="font-semibold">{u.operazione}</div><div className="text-xs text-muted-foreground">{u.codice} · <span className="capitalize">{u.stato}</span></div>
          <Riga l="Apporto complessivo" v={fmtEUR(u.apporto)} />
          {u.quota_concordata ? <Riga l="Quota utili concordata" v={fmtEUR(u.quota_concordata)} cls="text-emerald-700" /> : null}
          <Riga l="Ritenuta riportata (al primo pagamento)" v={fmtEUR(u.ritenuta_riportata)} cls="text-amber-700" />
          <Riga l="Utili lordi maturati finora nella catena" v={fmtEUR(d.righe.reduce((a, r) => a + (r.utili_stimati ? 0 : r.utili_lordi), 0))} cls="text-emerald-700" />
        </div>
      </div>
      <Dialog open={!!pdf} onOpenChange={() => { if (pdf) URL.revokeObjectURL(pdf); setPdf(null); }}>
        <DialogContent className="max-w-5xl h-[90vh] p-3 flex flex-col" data-testid="catena-rendiconto-dialog">
          <DialogHeader className="pb-1"><DialogTitle className="text-base">Rendiconto della tua posizione</DialogTitle></DialogHeader>
          {pdf && <iframe title="Rendiconto" src={pdf} className="flex-1 w-full rounded border" />}
        </DialogContent>
      </Dialog>
    </Card>
  );
}

export default function PortaleCatena({ invs }) {
  // una catena per ogni contratto finale (quello non ancora reinvestito in altro)
  const finali = invs.filter((i) => (i.reinvestimento_da_codice || i.reinvestito_in_codice) && !i.reinvestito_in_codice);
  if (finali.length === 0) return <Card className="p-6 text-sm text-muted-foreground" data-testid="portal-catena-vuota">Nessun reinvestimento: quando capitale e utili di un'operazione conclusa passeranno in una nuova operazione, qui vedrai la catena completa dei passaggi.</Card>;
  return <div className="space-y-4">{finali.map((i) => <Catena key={i.id} id={i.id} />)}</div>;
}
